import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { cn } from "../../lib/utils";

/** Valor interno da opção vazia. O Radix não aceita value="". */
const EMPTY = "__polaris_empty__";

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  size = "default",
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: "sm" | "default";
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        "flex w-fit items-center justify-between gap-2 rounded-md border border-input bg-[var(--surface-2)] px-3 py-2 text-sm whitespace-nowrap text-foreground shadow-xs transition-[color,box-shadow] outline-none",
        "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "aria-invalid:border-destructive aria-invalid:ring-destructive/20",
        "data-[placeholder]:text-muted-foreground data-[size=default]:h-9 data-[size=sm]:h-8",
        "*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn("flex cursor-default items-center justify-center py-1", className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn("flex cursor-default items-center justify-center py-1", className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

function SelectContent({
  className,
  children,
  position = "item-aligned",
  align = "center",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          "relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-md border border-border bg-[var(--surface-1)] text-foreground shadow-[var(--shadow-hover)]",
          "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        align={align}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn("px-2 py-1.5 text-xs text-muted-foreground", className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none",
        "focus:bg-[var(--surface-tint)] focus:text-[var(--accent-strong)] data-[highlighted]:bg-[var(--surface-tint)] data-[highlighted]:text-[var(--accent-strong)]",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        "*:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <span
        data-slot="select-item-indicator"
        className="absolute right-2 flex size-3.5 items-center justify-center"
      >
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("pointer-events-none -mx-1 my-1 h-px bg-border", className)}
      {...props}
    />
  );
}

export {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};

type Item = { value: string; label: React.ReactNode; disabled?: boolean };
type Block =
  | { kind: "item"; item: Item }
  | { kind: "group"; label: string; items: Item[] };

function textOf(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement(node))
    return textOf((node.props as { children?: React.ReactNode }).children);
  return "";
}

function asItem(node: React.ReactNode): Item | null {
  if (!React.isValidElement(node) || node.type !== "option") return null;
  const props = node.props as {
    value?: string | number;
    disabled?: boolean;
    children?: React.ReactNode;
  };
  const value =
    props.value === undefined ? textOf(props.children) : String(props.value);
  return { value, label: props.children ?? value, disabled: props.disabled };
}

/** Lê <option> e <optgroup>, na ordem, para montar o menu do Radix. */
function blocksFrom(children: React.ReactNode): Block[] {
  const blocks: Block[] = [];
  const visit = (nodes: React.ReactNode) => {
    React.Children.forEach(nodes, (child) => {
      if (!React.isValidElement(child)) return;
      const props = child.props as {
        children?: React.ReactNode;
        label?: string;
      };
      if (child.type === React.Fragment) {
        visit(props.children);
        return;
      }
      if (child.type === "optgroup") {
        const items: Item[] = [];
        React.Children.forEach(props.children, (opt) => {
          const item = asItem(opt);
          if (item) items.push(item);
        });
        blocks.push({ kind: "group", label: String(props.label ?? ""), items });
        return;
      }
      const item = asItem(child);
      if (item) blocks.push({ kind: "item", item });
    });
  };
  visit(children);
  return blocks;
}

function radixValue(value: string) {
  return value === "" ? EMPTY : value;
}

/**
 * Select da demonstração do shadcn. Aceita <option> e <optgroup> e o onChange
 * no formato do select nativo, para os formulários existentes.
 */
function Select({
  value,
  onChange,
  children,
  id,
  disabled,
  className,
  name,
  "aria-label": ariaLabel,
  "aria-describedby": describedBy,
  "aria-invalid": invalid,
}: {
  value?: string | number;
  onChange?: (event: { target: { value: string } }) => void;
  children?: React.ReactNode;
  id?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  const blocks = blocksFrom(children);
  const items = blocks.flatMap((b) => (b.kind === "item" ? [b.item] : b.items));
  const current = value == null ? "" : String(value);
  const hasEmpty = items.some((item) => item.value === "");
  const selected = current === "" ? (hasEmpty ? EMPTY : undefined) : current;
  let n = 0;
  const renderItem = (item: Item) => {
    const key = `${n++}-${item.value}`;
    return (
      <SelectItem key={key} value={radixValue(item.value)} disabled={item.disabled}>
        {item.label}
      </SelectItem>
    );
  };
  return (
    <SelectPrimitive.Root
      data-slot="select"
      value={selected}
      onValueChange={(next) =>
        onChange?.({ target: { value: next === EMPTY ? "" : next } })
      }
      disabled={disabled}
      name={name}
    >
      <SelectTrigger
        id={id}
        className={className}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {blocks.map((block, i) =>
          block.kind === "item" ? (
            <React.Fragment key={i}>{renderItem(block.item)}</React.Fragment>
          ) : (
            <SelectGroup key={i}>
              <SelectLabel>{block.label}</SelectLabel>
              {block.items.map(renderItem)}
            </SelectGroup>
          ),
        )}
      </SelectContent>
    </SelectPrimitive.Root>
  );
}

export { Select };
